import React, { useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import getTheme from './theme';
import AdminLayout from './components/Layout/AdminLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import UserDetail from './pages/Users/UserDetail';
import Recipes from './pages/Recipes';
import RecipeDetail from './pages/Recipes/RecipeDetail';
import Tags from './pages/Tags';
import Books from './pages/Books';
import Analytics from './pages/Analytics';
import Queues from './pages/Queues';
import Cache from './pages/Cache';

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isLoading, isAuthenticated, isAdmin } = useAuth();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        fontFamily: 'JetBrains Mono, monospace',
        backgroundColor: '#0a0a0b',
        color: '#fff',
      }}>
        <h1 style={{ marginBottom: 16 }}>Access Denied</h1>
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>You don't have admin privileges.</p>
      </div>
    );
  }

  return children;
};

// Main app content
const AppContent = () => {
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('admin-theme') || 'dark';
  });

  const theme = useMemo(() => getTheme(themeMode), [themeMode]);

  const toggleTheme = () => {
    setThemeMode((prev) => {
      const newMode = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('admin-theme', newMode);
      return newMode;
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AdminLayout themeMode={themeMode} toggleTheme={toggleTheme}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/users/:userId" element={<UserDetail />} />
                    <Route path="/recipes" element={<Recipes />} />
                    <Route path="/recipes/:recipeId" element={<RecipeDetail />} />
                    <Route path="/tags" element={<Tags />} />
                    <Route path="/books" element={<Books />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/queues" element={<Queues />} />
                    <Route path="/cache" element={<Cache />} />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
